/**
 * Editable state for each preview row and the pure mapping from the
 * caterer's decisions to the API calls the page will issue.
 */
import type { CreateIngredientRequest, IngredientCategory } from '@/types/api';
import type { ExistingItemRef, ImportPreview, ImportRow, ImportSource } from '@/types/procurementImport';
import { canonicalUnit, getAutoConversionMultiplier } from '@/types/units';
import { parseQuantityInput, roundProcurementQuantity } from '@/components/procurement/helpers';

export type ExistingDecision = 'use-sheet' | 'keep-recipe' | 'add-both';
export type RowGroup = 'attention' | 'merged' | 'ready' | 'kitchen' | 'left-out';

export interface ImportRowState {
  key: string;
  row: ImportRow;
  included: boolean;
  ingredientId: number | null;
  ingredientName: string | null;
  ingredientUnit: string | null;
  /** Text as typed; validated by getRowProblems */
  quantity: string;
  unit: string | null;
  existingDecision: ExistingDecision;
  createIngredient: { name: string; category: IngredientCategory; defaultUnit: string } | null;
  vendorId: number | null;
  /** The caterer chose the vendor (Unassigned included); untouched lets the server pick */
  vendorTouched: boolean;
  /** The caterer looked at a server-flagged total (edited it or clicked Looks right) */
  quantityChecked: boolean;
}

/** Largest purchase quantity the list stores (Decimal(10,4)), in whole units. */
export const MAX_IMPORT_QUANTITY = 999999;

/** vendorId: omitted = server default, null = Unassigned on purpose. */
export interface ConfirmPlan {
  creates: { key: string; request: CreateIngredientRequest; vendorId: number | null }[];
  /** rawQuantity: the summed sheet total before rounding, so a unit conversion at apply time rounds once */
  manualAdds: { key: string; ingredientId: number | null; purchaseQuantity: number; rawQuantity?: number; vendorId?: number | null; notes: string | null }[];
  /** MANUAL rows get the imported amount; RECIPE and STAPLE rows only ever get isExcluded */
  updates: { key: string; itemId: number; purchaseQuantity?: number; isExcluded?: boolean; vendorId?: number | null; notes?: string }[];
  problems: { key: string; message: string }[];
  itemCount: number;
}

export function createRowStates(preview: ImportPreview, existingItems: ExistingItemRef[] = []): ImportRowState[] {
  return preview.rows.map((row) => {
    // Suggestions stay unresolved until the caterer picks one.
    const match = row.matchStatus === 'confident' ? row.ingredient : null;
    const manual = match ? findManualRow(resolveExistingRows(match.id, existingItems)) : null;
    return {
      key: row.key,
      row,
      included: !row.issues.includes('in-kitchen'),
      ingredientId: match?.id ?? null,
      ingredientName: match?.name ?? null,
      ingredientUnit: match?.defaultUnit ?? null,
      quantity: seededQuantity(row),
      unit: row.unit,
      existingDecision: 'use-sheet',
      createIngredient: null,
      vendorId: manual ? manual.vendorId ?? null : match ? row.preferredVendor?.id ?? null : null,
      vendorTouched: false,
      quantityChecked: false,
    };
  });
}

// Keyword → category for the create form's default; first match wins.
const CATEGORY_KEYWORDS: [IngredientCategory, string[]][] = [
  ['FROZEN', ['frozen']],
  ['MEAT', ['chicken', 'mutton', 'beef', 'lamb']],
  ['DAIRY', ['milk', 'cheese', 'butter', 'paneer', 'yogurt', 'ghee']],
  ['SPICES', ['masala', 'cumin', 'turmeric', 'haldi', 'jeera']],
  ['PRODUCE', ['onion', 'tomato', 'potato', 'garlic', 'ginger', 'chili', 'cilantro', 'spinach', 'squash']],
];

/** Default category for a new ingredient, guessed from the sheet's name. */
export function guessCategory(rawName: string): IngredientCategory {
  const tokens = new Set(rawName.toLowerCase().split(/[^a-z]+/));
  for (const [category, words] of CATEGORY_KEYWORDS) {
    if (words.some((word) => tokens.has(word))) return category;
  }
  return 'OTHER';
}

function seededQuantity(row: ImportRow): string {
  return row.quantity === null ? '' : String(row.quantity);
}

/** Every list row for an ingredient, excluded ones included. */
export function resolveExistingRows(ingredientId: number | null, existingItems: ExistingItemRef[]): ExistingItemRef[] {
  if (ingredientId === null) return [];
  return existingItems.filter((item) => item.ingredientId === ingredientId);
}

/** Rows for the state's chosen ingredient; none while creating a new one. */
export function existingRowsFor(state: ImportRowState, existingItems: ExistingItemRef[]): ExistingItemRef[] {
  return state.createIngredient ? [] : resolveExistingRows(state.ingredientId, existingItems);
}

/** The MANUAL row an import writes into, if the list has one. */
export function findManualRow(rows: ExistingItemRef[]): ExistingItemRef | null {
  return rows.find((item) => item.sourceType === 'MANUAL') ?? null;
}

/** Live RECIPE and STAPLE rows: the amounts the list already buys for this ingredient. */
export function liveListRows(rows: ExistingItemRef[]): ExistingItemRef[] {
  return rows.filter((item) => item.sourceType !== 'MANUAL' && !item.isExcluded);
}

const SOURCE_LABELS: Record<ExistingItemRef['sourceType'], string> = { RECIPE: 'recipe', STAPLE: 'staple', MANUAL: 'manual' };

function formatAmount(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? String(roundProcurementQuantity(n)) : value;
}

function partLabel(item: ExistingItemRef, withUnit: boolean): string {
  const fromImport = item.sourceType === 'MANUAL' && isImportOwned(item) ? ', from an earlier import' : '';
  return `${formatAmount(item.purchaseQuantity)}${withUnit ? ` ${item.unit}` : ''} ${SOURCE_LABELS[item.sourceType]}${fromImport}`;
}

/** "On the list: 25 lbs (20 recipe + 5 staple)", with excluded rows listed after. */
export function describeOnList(rows: ExistingItemRef[]): string {
  const live = rows.filter((item) => !item.isExcluded);
  const excluded = rows.filter((item) => item.isExcluded).map((item) => `${partLabel(item, true)} (excluded)`);
  let text = '';
  if (live.length > 0) {
    const unit = live[0]!.unit;
    if (live.every((item) => item.unit === unit)) {
      const total = roundProcurementQuantity(live.reduce((sum, item) => sum + (Number(item.purchaseQuantity) || 0), 0));
      text = `${total} ${unit} (${live.map((item) => partLabel(item, false)).join(' + ')})`;
    } else {
      text = live.map((item) => partLabel(item, true)).join(' + ');
    }
  }
  return `On the list: ${[text, ...excluded].filter(Boolean).join('; ')}`;
}

function normalizeCreateName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Rows sharing this key are combined into one list item at confirm. */
function combineKey(state: ImportRowState): string | null {
  if (state.createIngredient) return `new:${normalizeCreateName(state.createIngredient.name)}`;
  return state.ingredientId === null ? null : `ing:${state.ingredientId}`;
}

/** Raw names of the other included rows that will be combined with this one. */
export function combinedWith(state: ImportRowState, all: ImportRowState[]): string[] {
  const key = combineKey(state);
  if (!state.included || key === null) return [];
  return all.filter((other) => other !== state && other.included && combineKey(other) === key).map((other) => other.row.rawName);
}

function convertInto(state: ImportRowState, target: string | null): number | null {
  const parsed = Number(state.quantity);
  if (!Number.isFinite(parsed) || parsed <= 0 || !state.unit) return null;
  if (!target || target === state.unit) return parsed;
  const multiplier = getAutoConversionMultiplier(state.unit, target);
  return multiplier === null ? null : roundProcurementQuantity(parsed * multiplier);
}

function convertProblem(from: string, to: string): string | null {
  return to !== from && getAutoConversionMultiplier(from, to) === null ? `Can't convert ${from} to ${to}; change the unit` : null;
}

const TOO_LARGE = `Quantity is too large for the list (max ${MAX_IMPORT_QUANTITY.toLocaleString('en-US')})`;

/** Unit the imported amount is stored in (canonical): the MANUAL row's, else the ingredient's. */
function targetUnitFor(state: ImportRowState, rows: ExistingItemRef[]): string | null {
  const unit = findManualRow(rows)?.unit ?? state.createIngredient?.defaultUnit ?? state.ingredientUnit;
  return unit === null ? null : canonicalUnit(unit) ?? unit;
}

/** Whether a keep row still buys its amount: it does when its group uses the sheet amount. */
function keepRowContributes(state: ImportRowState, all: ImportRowState[]): boolean {
  const key = combineKey(state);
  const group = key === null ? [state] : all.filter((other) => other.included && combineKey(other) === key);
  return groupDecision(group.length > 0 ? group : [state]) === 'use-sheet';
}

/** A server-flagged total the caterer has not edited or accepted yet. */
export function quantityNeedsCheck(state: ImportRowState): boolean {
  const { row } = state;
  const flagged = row.issues.includes('no-quantity') || row.issues.includes('unknown-unit') || row.issues.includes('ambiguous-quantity');
  return state.included && flagged && !state.quantityChecked && state.quantity === seededQuantity(row);
}

export function getRowProblems(state: ImportRowState, all: ImportRowState[], existingItems: ExistingItemRef[] = []): string[] {
  if (!state.included) return [];
  const problems: string[] = [];
  const { row } = state;

  // A row that keeps the list amount contributes nothing, so its sheet quantity needs no fixing.
  if (!state.createIngredient && state.ingredientId !== null) {
    const rows = existingRowsFor(state, existingItems);
    if (liveListRows(rows).length > 0 && state.existingDecision === 'keep-recipe' && !keepRowContributes(state, all)) return [];
  }

  const parsed = parseQuantityInput(state.quantity);
  if (state.quantity.trim() === '' || parsed === null) problems.push('Enter a quantity');
  else if (parsed <= 0) problems.push('Enter a quantity greater than 0');

  if (quantityNeedsCheck(state)) {
    if (row.issues.includes('no-quantity') || row.issues.includes('unknown-unit')) {
      const uncounted = row.sources.filter((s) => s.convertedQuantity === null).length;
      problems.push(`${uncounted} of ${row.sources.length} sheet lines couldn't be counted. Check the total, then click Looks right or type the right amount`);
    }
    if (row.issues.includes('ambiguous-quantity')) {
      problems.push(`Sheet says "${row.rawQuantity}". Check the total, then click Looks right or type the right amount`);
    }
  }

  if (!state.unit) problems.push('Pick a unit');

  if (state.ingredientId === null && !state.createIngredient) {
    problems.push('Pick an ingredient or create one');
  } else if (state.ingredientId !== null && !state.createIngredient && state.ingredientUnit === null) {
    problems.push('Pick the ingredient again to set its unit');
  } else if (state.unit) {
    const rows = existingRowsFor(state, existingItems);
    const hasRecipe = liveListRows(rows).length > 0;
    const manual = findManualRow(rows);
    const target = targetUnitFor(state, rows);
    const leftOut = hasRecipe && state.existingDecision === 'keep-recipe' && !keepRowContributes(state, all);
    if (!leftOut && target) {
      if (manual && convertProblem(state.unit, target)) {
        problems.push(`The list has ${state.ingredientName ?? 'this ingredient'} in ${target}; the sheet says ${state.unit}. Change the unit${hasRecipe ? ', or choose "keeping the list amount"' : ''}.`);
      } else {
        const problem = convertProblem(state.unit, target);
        if (problem) problems.push(problem);
        else if ((convertInto(state, target) ?? 0) > MAX_IMPORT_QUANTITY) problems.push(TOO_LARGE);
      }
    }
  }

  if (state.createIngredient && state.vendorId === null) problems.push('Pick a vendor');

  return problems;
}

export function rowGroup(state: ImportRowState, problems: string[], existingItems: ExistingItemRef[] = []): RowGroup {
  if (!state.included) return state.row.issues.includes('in-kitchen') ? 'kitchen' : 'left-out';
  if (problems.length > 0) return 'attention';
  if (state.row.sources.length > 1 || existingRowsFor(state, existingItems).length > 0) return 'merged';
  return 'ready';
}

export function describeSources(sources: ImportSource[]): string {
  const parts = sources.map((s) => `${s.day ?? 'week'} ${s.rawQuantity || '?'}${s.dish ? ` (${s.dish})` : ''}`);
  return `From sheet: ${parts.join(', ')}`;
}

const SOURCES_PREFIX = 'From sheet: ';

// Semicolons separate note segments, so the sheet's own text must not contain one.
function rowSources(state: ImportRowState): string {
  const base = describeSources(state.row.sources).slice(SOURCES_PREFIX.length);
  return (state.row.notes ? `${base} — ${state.row.notes}` : base).replace(/;/g, ',');
}

/** "From sheet (+30 lbs): Mon 30 lb, …" — the tag records what this import added. */
function sheetTag(amount: number, unit: string | null, group: ImportRowState[]): string {
  return `From sheet (+${amount}${unit ? ` ${unit}` : ''}): ${group.map(rowSources).join(', ')}`;
}

const MAX_NOTES_LENGTH = 5000;
const TAG_PATTERN = /From sheet \(\+([\d.]+)(?: ([^)]*))?\):/;
// A tag segment runs to the next ";" or the end of the notes.
const TAG_SEGMENT = /From sheet \(\+[\d.]+(?: [^)]*)?\):[^;]*/;

const DECISION_STRENGTH: Record<ExistingDecision, number> = { 'add-both': 2, 'use-sheet': 1, 'keep-recipe': 0 };

function groupDecision(group: ImportRowState[]): ExistingDecision {
  return group.reduce<ExistingDecision>((best, s) => (DECISION_STRENGTH[s.existingDecision] > DECISION_STRENGTH[best] ? s.existingDecision : best), 'keep-recipe');
}

/** A MANUAL row an earlier import contributed to. */
export function isImportOwned(item: ExistingItemRef): boolean {
  return item.notes ? TAG_PATTERN.test(item.notes) : false;
}

/** What an earlier import added to this row, in the row's unit; 0 when untagged or unconvertible. */
function previousContribution(item: ExistingItemRef): number {
  const match = item.notes?.match(TAG_PATTERN);
  if (!match) return 0;
  const amount = Number(match[1]);
  const unit = match[2]?.trim() || item.unit;
  const multiplier = unit === item.unit ? 1 : getAutoConversionMultiplier(unit, item.unit);
  return Number.isFinite(amount) && multiplier !== null ? amount * multiplier : 0;
}

/** Replaces the previous tag segment with the new one, keeping hand-typed text around it. */
function replaceSheetNote(existing: string | null, tag: string): string {
  const current = existing?.trim() ?? '';
  const joined = !current ? tag : TAG_SEGMENT.test(current) ? current.replace(TAG_SEGMENT, () => tag) : `${current}; ${tag}`;
  return joined.length <= MAX_NOTES_LENGTH ? joined : tag.slice(0, MAX_NOTES_LENGTH);
}

// Rounds float noise (2.3 + 20.7) before the ceiling; tiny positive amounts still buy 1.
function ceilQuantity(value: number): number {
  const rounded = roundProcurementQuantity(value);
  return Math.ceil(rounded > 0 ? rounded : value);
}

type SumResult = { total: number } | { problem: { key: string; message: string } };

function sumInto(group: ImportRowState[], target: string | null): SumResult {
  let total = 0;
  for (const state of group) {
    const converted = convertInto(state, target);
    if (converted === null) {
      return { problem: { key: state.key, message: convertProblem(state.unit ?? '', target ?? '') ?? 'Enter a quantity' } };
    }
    total += converted;
  }
  return { total };
}

export function buildConfirmPlan(states: ImportRowState[], existingItems: ExistingItemRef[] = []): ConfirmPlan {
  const plan: ConfirmPlan = { creates: [], manualAdds: [], updates: [], problems: [], itemCount: 0 };

  // Included, problem-free rows grouped by the ingredient they resolve to.
  const groups = new Map<string, ImportRowState[]>();
  for (const state of states) {
    if (!state.included) continue;
    const problems = getRowProblems(state, states, existingItems);
    if (problems.length > 0) {
      plan.problems.push({ key: state.key, message: problems[0]! });
      continue;
    }
    const key = combineKey(state)!;
    groups.set(key, [...(groups.get(key) ?? []), state]);
  }

  for (const group of groups.values()) {
    const first = group[0]!;
    const rows = existingRowsFor(first, existingItems);
    const listRows = liveListRows(rows);
    const manual = findManualRow(rows);

    // Strongest decision wins: adding both > using the sheet amount > keeping the list amount.
    const decision = groupDecision(group);
    const excludeList = listRows.length > 0 && decision === 'use-sheet';
    // Keep rows are left out only while the list's own rows stay live.
    const contributing = listRows.length > 0 && !excludeList ? group.filter((s) => s.existingDecision !== 'keep-recipe') : group;
    if (contributing.length === 0) continue;

    const target = targetUnitFor(first, rows);
    const summed = sumInto(contributing, target);
    if ('problem' in summed) { plan.problems.push(summed.problem); continue; }
    const purchaseQuantity = ceilQuantity(summed.total);
    if (purchaseQuantity > MAX_IMPORT_QUANTITY) { plan.problems.push({ key: first.key, message: TOO_LARGE }); continue; }

    const touched = group.find((s) => s.vendorTouched);
    const vendor = touched ? { vendorId: touched.vendorId } : {};
    const note = sheetTag(purchaseQuantity, target, contributing);

    if (manual) {
      // Swap the previous import's share for this one; the hand-typed share stays and never goes below 0.
      const handTyped = Math.max(0, (Number(manual.purchaseQuantity) || 0) - previousContribution(manual));
      const quantity = ceilQuantity(handTyped + purchaseQuantity);
      if (quantity > MAX_IMPORT_QUANTITY) { plan.problems.push({ key: first.key, message: TOO_LARGE }); continue; }
      plan.updates.push({ key: first.key, itemId: manual.id, purchaseQuantity: quantity, ...vendor, notes: replaceSheetNote(manual.notes ?? null, note) });
    } else {
      if (first.createIngredient) {
        plan.creates.push({ key: first.key, request: { ...first.createIngredient }, vendorId: first.vendorId });
      }
      plan.manualAdds.push({
        key: first.key,
        ingredientId: first.createIngredient ? null : first.ingredientId,
        purchaseQuantity,
        rawQuantity: summed.total,
        // A new ingredient has no preferred vendor yet, so its pick is always sent.
        ...(first.createIngredient ? { vendorId: first.vendorId } : vendor),
        notes: note,
      });
    }
    if (excludeList) {
      // The sheet amount replaces what the list was buying: recipe and staple rows are struck out.
      for (const item of listRows) plan.updates.push({ key: first.key, itemId: item.id, isExcluded: true });
    }
  }

  // Exclusions ride along with an add/update and are not counted separately.
  const amountUpdates = plan.updates.filter((u) => u.purchaseQuantity !== undefined).length;
  plan.itemCount = plan.problems.length > 0 ? 0 : plan.manualAdds.length + amountUpdates;
  return plan;
}
